import { useState, useEffect, useCallback } from "react";
import {
  useNavigate,
  useParams,
  useLocation,
  useSearchParams,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Grid,
  FormControlLabel,
  Radio,
  RadioGroup,
  Autocomplete,
  CircularProgress,
  Card,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack,
  Save,
  Cancel,
  InfoOutlined,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd,
} from "@mui/icons-material";
import { donationsAPI, contactsAPI, donationSchemasAPI } from "../services/api";
import { colors } from "../theme/modernTheme";
import { showSuccess, showError } from "../utils/toast";
import {
  getTodayDate,
  getDateWithOffset,
  getDateWithYearOffset,
} from "../utils/dateUtils";
import { useAuth } from "../context/AuthContext";
import DonationSchemaAutocomplete from "../components/DonationSchemaAutocomplete";
import DonationConfirmationWrapper from "../components/DonationConfirmationWrapper";
import DeleteConfirmationDialog from "../components/DeleteConfirmationDialog";
import { MODULE_PERMISSIONS } from "../utils/modulePermissions";
import {
  saveDonationFormState,
  clearDonationFormState,
  selectSavedDonationFormState,
} from "../store/slices/donationFormSlice";

export default function DonationFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const savedFormState = useSelector(selectSavedDonationFormState);
  const contactIdFromState = location.state?.contactId;
  const newContactId = searchParams.get("newContactId"); // Get newly created contact ID from URL
  const { user, hasModulePermission } = useAuth();
  const contactsPermission = hasModulePermission(MODULE_PERMISSIONS.CONTACTS);

  // Determine initial mode: 'view' if id exists and no mode param, 'edit' otherwise
  const initialMode =
    id && id !== "new" && searchParams.get("mode") !== "edit" ? "view" : "edit";
  const [mode, setMode] = useState(initialMode);

  const [formData, setFormData] = useState({
    contact_id: contactIdFromState || "",
    donation_date: getTodayDate(),
    amount: "",
    currency: "INR",
    donation_type: "one_time", // one_time, recurring, or pledge
    frequency: "monthly", // Only used when donation_type is recurring
    recurring_start_date: getDateWithOffset(1), // Default: tomorrow (+1 day)
    recurring_end_date: getDateWithYearOffset(1), // Default: next year
    purpose: "",
    donation_schema: null, // Changed to store schema object instead of name
    donation_schema_name: "", // For display purposes when schema object is not available
    notes: "",
    first_name: "", // Add contact fields from API
    last_name: "",
    organization_name: "",
    primary_email: "",
    recorded_by_name: "",
  });
  const [contacts, setContacts] = useState([]);
  const [donationSchemas, setDonationSchemas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [contactsPage, setContactsPage] = useState(1);
  const [hasMoreContacts, setHasMoreContacts] = useState(true);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [contactSearchTerm, setContactSearchTerm] = useState("");
  const [debouncedContactSearch, setDebouncedContactSearch] = useState("");

  console.log({ validationErrors });
  console.log({ fieldErrors });

  const [schemasPage, setSchemasPage] = useState(1);
  const [hasMoreSchemas, setHasMoreSchemas] = useState(true);
  const [loadingSchemas, setLoadingSchemas] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Debounce contact search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedContactSearch(contactSearchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [contactSearchTerm]);

  // Load contacts when debounced search changes
  useEffect(() => {
    if (debouncedContactSearch !== undefined) {
      loadContacts(1, debouncedContactSearch);
    }
  }, [debouncedContactSearch]);

  // Validation functions
  const validateFieldWithFormData = (name, value, currentFormData) => {
    let error = "";

    switch (name) {
      case "contact_id":
        if (!value) {
          error = "Donor is required";
        }
        break;

      case "amount":
        if (!value || value === "") {
          error = "Amount is required";
        } else if (parseFloat(value) <= 0) {
          error = "Amount must be greater than 0";
        } else if (parseFloat(value) > 500000000) {
          error = "Amount cannot exceed 500,000,000";
        } else if (!Number.isInteger(parseFloat(value))) {
          error = "Amount must be a whole number";
        } else if (value.toString().startsWith("0")) {
          error = "Amount cannot start with 0";
        }
        break;

      case "donation_date":
        if (!value) {
          error = "Donation date is required";
        }
        break;

      case "donation_schema":
        if (!value) {
          error = "Donation schema is required";
        }
        break;

      case "recurring_start_date":
        if (currentFormData.donation_type === "recurring" && !value) {
          error = "Recurring start date is required for recurring donations";
        } else if (value) {
          const startDate = new Date(value);
          startDate.setHours(0, 0, 0, 0);
          // Validate against recurring_end_date if it exists
          if (currentFormData.recurring_end_date) {
            const endDate = new Date(currentFormData.recurring_end_date);
            endDate.setHours(0, 0, 0, 0);
            if (startDate > endDate) {
              error =
                "Recurring start date must be equal to or before recurring end date";
            }
          }
        }
        break;
      case "recurring_end_date":
        if (value && currentFormData.recurring_start_date) {
          const startDate = new Date(currentFormData.recurring_start_date);
          const endDate = new Date(value);
          startDate.setHours(0, 0, 0, 0);
          endDate.setHours(0, 0, 0, 0);
          if (endDate < startDate) {
            error =
              "Recurring end date must be equal to or after recurring start date";
          }
        }
        break;

      case "currency":
        if (!value) {
          error = "Currency is required";
        }
        break;

      case "frequency":
        if (currentFormData.donation_type === "recurring" && !value) {
          error = "Frequency is required";
        }
        break;

      default:
        break;
    }

    return error;
  };

  const validateField = (name, value) => {
    return validateFieldWithFormData(name, value, formData);
  };

  const validateForm = () => {
    const errors = {};

    // Required fields
    errors.contact_id = validateField("contact_id", formData.contact_id);
    errors.amount = validateField("amount", formData.amount);
    errors.donation_date = validateField(
      "donation_date",
      formData.donation_date
    );
    errors.donation_schema = validateField(
      "donation_schema",
      formData.donation_schema
    );
    errors.currency = validateField("currency", formData.currency);
    errors.frequency = validateField("frequency", formData.frequency);

    // Conditional validations
    if (formData.donation_type === "recurring") {
      errors.recurring_start_date = validateField(
        "recurring_start_date",
        formData.recurring_start_date
      );
      errors.frequency = validateField("frequency", formData.frequency);
    }

    // Remove empty errors
    Object.keys(errors).forEach((key) => {
      if (!errors[key]) delete errors[key];
    });

    return errors;
  };

  // isFormValid removed - save button is always enabled
  // const isFormValid = React.useMemo(() => {
  //   ... validation logic removed
  // }, [formData, validationErrors]);

  const handleEditClick = () => {
    setMode("edit");
    navigate(`/donations/${id}?mode=edit`, { replace: true });
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setDeleting(true);
      await donationsAPI.delete(id);
      showSuccess("Donation deleted successfully");
      navigate("/donations");
    } catch (error) {
      showError(error?.error?.message || "Failed to delete donation");
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleCancelEdit = () => {
    if (id && id !== "new") {
      setMode("view");
      navigate(`/donations/${id}`, { replace: true });
      // Reload donation data to reset any changes
      loadDonation();
    } else {
      navigate("/donations");
    }
  };

  // Check if user has edit permissions
  const canEdit = user?.role === "ADMIN" || user?.role === "STAFF";
  const canDelete = user?.role === "ADMIN";
  const isViewMode = mode === "view";
  const isEditMode = mode === "edit";

  const loadDonation = async () => {
    setPageLoading(true);
    try {
      const response = await donationsAPI.getOne(id);
      const donation = response.data;

      // Load schemas first to convert schema ID to object
      const schemasResponse = await donationSchemasAPI.getAll({
        limit: 1000,
        sortBy: "name",
        sortOrder: "asc",
      });
      const schemas = schemasResponse.data || [];

      // Find the schema object that matches the donation_schema ID
      const schemaObject =
        schemas.find((s) => s.id === donation.donation_schema) || null;

      // Determine donation_type from is_recurring and is_pledge
      let donationType = "one_time";
      if (donation.is_pledge) {
        donationType = "pledge";
      } else if (donation.is_recurring) {
        donationType = "recurring";
      }

      // Handle the API response structure properly
      setFormData({
        // Core donation fields
        id: donation.id,
        contact_id: donation.contact_id,
        recorded_by_user_id: donation.recorded_by_user_id,
        amount: donation.amount,
        currency: donation.currency || "INR",
        frequency: donation.frequency || "monthly",
        is_recurring: donation.is_recurring,
        is_pledge: donation.is_pledge,
        purpose: donation.purpose || "",
        notes: donation.notes || "",
        donation_schema: schemaObject, // Convert ID to object for autocomplete
        donation_schema_name:
          donation.donation_schema_name || schemaObject?.name || "",
        created_at: donation.created_at,
        updated_at: donation.updated_at,

        // Date fields - backend returns YYYY-MM-DD format from DATE()
        donation_date: donation.donation_date || "",

        // Contact information from API response
        first_name: donation.first_name || "",
        last_name: donation.last_name || "",
        organization_name: donation.organization_name || "",
        primary_email: donation.primary_email || "",
        recorded_by_name: donation.recorded_by_name || "",

        // Derived fields
        donation_type: donationType,

        // Recurring fields with proper defaults (now from reminders table)
        recurring_start_date:
          donation.recurring_start_date ||
          (donation.is_recurring ? getDateWithOffset(1) : ""),
        recurring_end_date:
          donation.recurring_end_date ||
          (donation.is_recurring ? getDateWithYearOffset(1) : ""),
      });
    } catch (err) {
      showError("Failed to load donation");
      navigate("/donations");
    } finally {
      setPageLoading(false);
    }
  };

  const loadContactIfNeeded = async (contactId) => {
    if (!contactId) return;

    // Check if we already have this contact in the list
    const existingContact = contacts.find((c) => c.id === contactId);
    if (existingContact) return;

    try {
      const response = await contactsAPI.get(contactId);
      const contact = response.data;
      if (contact) {
        setContacts((prev) => {
          if (prev.find((c) => c.id === contact.id)) return prev;
          return [...prev, contact];
        });
      }
    } catch (err) {
      console.error("Failed to load selected contact", err);
      // If API call fails but we have contact data from donation API, create a contact object
      if (formData.first_name || formData.organization_name) {
        const contactFromDonationData = {
          id: contactId,
          first_name: formData.first_name,
          last_name: formData.last_name,
          organization_name: formData.organization_name,
          primary_email: formData.primary_email,
          type: formData.organization_name ? "ORGANIZATION" : "INDIVIDUAL",
        };
        setContacts((prev) => {
          if (prev.find((c) => c.id === contactId)) return prev;
          return [...prev, contactFromDonationData];
        });
      }
    }
  };

  useEffect(() => {
    if (formData.contact_id) {
      // If we have a contact_id but it's not in our list (which starts small), fetch it
      const isInList = contacts.some((c) => c.id === formData.contact_id);
      if (!isInList && contacts.length > 0) {
        // Only check if contacts list has initialized
        loadContactIfNeeded(formData.contact_id);
      }
    }
  }, [formData.contact_id, contacts.length]);

  // Handle newly created contact from redirect
  useEffect(() => {
    console.log("=== NEW CONTACT REDIRECT EFFECT ===");
    console.log("newContactId:", newContactId);
    console.log("id:", id);
    console.log("mode:", mode);

    // Only run when creating a new donation (id === "new" or undefined) and we have a newContactId
    if (newContactId && (id === "new" || !id)) {
      const contactIdNum = parseInt(newContactId);

      if (savedFormState) {
        // Restore form data with the new contact ID
        setFormData((prev) => ({
          ...savedFormState,
          contact_id: contactIdNum,
        }));

        // Clear the saved state from Redux
        dispatch(clearDonationFormState());
      } else {
        // No saved state, just set the contact_id
        setFormData((prev) => ({
          ...prev,
          contact_id: contactIdNum,
        }));
      }

      // Load the contact if not in list

      loadContactIfNeeded(contactIdNum);
    }
  }, [newContactId, id]);

  const loadContacts = useCallback(async (pageNum = 1, searchTerm = "") => {
    try {
      setLoadingContacts(true);
      const params = {
        limit: 20,
        page: pageNum,
        sortBy: "created_at",
        sortOrder: "desc",
        dropdown: true,
      };

      // Add search parameter if provided
      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await contactsAPI.getAll(params);

      setContacts((prev) => {
        if (pageNum === 1) return response.data;
        // Filter out duplicates just in case
        const newContacts = response.data.filter(
          (newC) => !prev.some((existingC) => existingC.id === newC.id)
        );
        return [...prev, ...newContacts];
      });

      const total = response.pagination?.total || 0;
      const loadedCount = (pageNum - 1) * 20 + response.data.length;
      setHasMoreContacts(loadedCount < total);
      setContactsPage(pageNum);
    } catch (err) {
      showError("Failed to load contacts");
    } finally {
      setLoadingContacts(false);
    }
  }, []);

  const loadDonationSchemas = useCallback(async (pageNum = 1) => {
    try {
      setLoadingSchemas(true);
      const response = await donationSchemasAPI.getAll({
        limit: 20,
        page: pageNum,
        sortBy: "name",
        sortOrder: "asc",
        dropdown: true,
      });

      setDonationSchemas((prev) => {
        if (pageNum === 1) return response.data;
        // Filter out duplicates
        const newSchemas = response.data.filter(
          (newS) => !prev.some((existingS) => existingS.id === newS.id)
        );
        return [...prev, ...newSchemas];
      });

      const total = response.pagination?.total || 0;
      const loadedCount = (pageNum - 1) * 20 + response.data.length;
      setHasMoreSchemas(loadedCount < total);
      setSchemasPage(pageNum);
    } catch (err) {
      showError("Failed to load donation schemas");
    } finally {
      setLoadingSchemas(false);
    }
  }, []);

  useEffect(() => {
    loadContacts(1, ""); // Load initial contacts without search
    loadDonationSchemas();
    if (id && id !== "new") {
      loadDonation();
    }
  }, [id, loadDonationSchemas]);

  const handleChange = useCallback(
    (e) => {
      const { name, value, checked, type } = e.target;

      if (name === "donation_type") {
        setFormData((prev) => {
          console.log("Donation type changed to:", value);
          console.log("Previous state:", {
            frequency: prev.frequency,
            recurring_start_date: prev.recurring_start_date,
            recurring_end_date: prev.recurring_end_date,
          });

          // Determine what fields to set based on donation type
          let updates = {
            ...prev,
            donation_type: value,
            // Update is_recurring and is_pledge flags
            is_recurring: value === "recurring",
            is_pledge: value === "pledge",
          };

          if (value === "recurring") {
            // Switching to recurring - set recurring fields with proper defaults
            updates.frequency = "monthly";
            updates.recurring_start_date =
              prev.recurring_start_date || getDateWithOffset(1); // Tomorrow
            updates.recurring_end_date =
              prev.recurring_end_date || getDateWithYearOffset(1); // Next year
          } else {
            // one_time or pledge - clear recurring fields
            updates.frequency = "";
            updates.recurring_start_date = "";
            updates.recurring_end_date = "";
          }

          return updates;
        });

        // Clear validation errors for fields that are no longer relevant
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          if (value !== "recurring") {
            delete newErrors.recurring_start_date;
            delete newErrors.recurring_end_date;
            delete newErrors.frequency;
          } else {
            // When switching TO recurring, clear any existing errors for these fields
            // so they don't prevent the fields from showing their values
            delete newErrors.frequency;
            delete newErrors.recurring_end_date;
          }
          return newErrors;
        });

        // Also clear field errors when switching donation types
        setFieldErrors((prev) => {
          const newErrors = { ...prev };
          if (value !== "recurring") {
            delete newErrors.recurring_start_date;
            delete newErrors.recurring_end_date;
            delete newErrors.frequency;
          } else {
            // When switching TO recurring, clear any existing field errors
            delete newErrors.frequency;
            delete newErrors.recurring_end_date;
          }
          return newErrors;
        });
      } else {
        const newValue = type === "checkbox" ? checked : value;

        setFormData((prev) => {
          const updatedFormData = {
            ...prev,
            [name]: newValue,
          };

          // For date fields, validate both dates with the updated data
          if (
            name === "recurring_start_date" ||
            name === "recurring_end_date"
          ) {
            // Validate both date fields immediately with the updated form data
            setValidationErrors((prevErrors) => {
              const newErrors = { ...prevErrors };

              // Validate both date fields with updated form data
              if (updatedFormData.donation_type === "recurring") {
                const startDateError = validateFieldWithFormData(
                  "recurring_start_date",
                  updatedFormData.recurring_start_date,
                  updatedFormData
                );
                const endDateError = validateFieldWithFormData(
                  "recurring_end_date",
                  updatedFormData.recurring_end_date,
                  updatedFormData
                );

                // Update errors for both fields (clear if no error)
                newErrors.recurring_start_date = startDateError || "";
                newErrors.recurring_end_date = endDateError || "";
              } else {
                // Clear both date errors if not recurring
                newErrors.recurring_start_date = "";
                newErrors.recurring_end_date = "";
              }

              return newErrors;
            });
          } else {
            // For non-date fields, validate immediately and clear field errors
            const error = validateFieldWithFormData(
              name,
              newValue,
              updatedFormData
            );
            setValidationErrors((prev) => ({
              ...prev,
              [name]: error,
            }));

            // Clear field errors when user starts typing
            if (fieldErrors[name]) {
              setFieldErrors((prev) => ({
                ...prev,
                [name]: "",
              }));
            }
          }

          return updatedFormData;
        });
      }
    },
    [fieldErrors]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submission
    const errors = validateForm();
    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      // Don't show error message - field errors are displayed below each field
      return;
    }

    setLoading(true);
    setFieldErrors({});

    try {
      const submitData = {
        ...formData,
        // Convert schema object to ID for API
        donation_schema:
          formData.donation_schema?.id || formData.donation_schema,
        // Set is_recurring and is_pledge based on donation_type
        is_recurring: formData.donation_type === "recurring",
        is_pledge: formData.donation_type === "pledge",
        // Set frequency to one_time if not recurring
        frequency:
          formData.donation_type === "recurring"
            ? formData.frequency
            : "one_time",
      };

      // Only include recurring fields for recurring donations
      if (formData.donation_type === "recurring") {
        submitData.recurring_start_date = formData.recurring_start_date;
        submitData.recurring_end_date = formData.recurring_end_date || null;
      } else {
        // Remove recurring fields for non-recurring donations
        delete submitData.recurring_start_date;
        delete submitData.recurring_end_date;
      }

      // If updating a recurring donation, show confirmation dialog
      if (id && id !== "new" && formData.donation_type === "recurring") {
        setPendingSubmitData(submitData);
        setConfirmDialogOpen(true);
        setLoading(false);
        return;
      }

      // Otherwise, proceed with submission
      await performSubmit(submitData);
    } catch (err) {
      console.error("Submit Error:", err);
      // Error already handled in performSubmit, just reset loading state
      setLoading(false);
    }
  };

  const performSubmit = async (submitData) => {
    try {
      if (id && id !== "new") {
        await donationsAPI.update(id, submitData);
        showSuccess("Donation updated successfully");
      } else {
        await donationsAPI.create(submitData);
        showSuccess("Donation created successfully");
      }
      navigate("/donations");
    } catch (err) {
      console.error("API Error:", err);

      // Handle different types of API errors
      if (err.error?.code === "VALIDATION_ERROR") {
        const errors = err.error.details?.field_errors || {};
        setValidationErrors(errors);
        // Don't show error message - field errors are displayed below each field
      } else if (err.error?.message) {
        // API returned a structured error
        showError(err.error.message);
      } else if (err.message) {
        // JavaScript error or network error
        showError(err.message);
      } else if (err.response?.data?.error?.message) {
        // Axios error with nested error structure
        showError(err.response.data.error.message);
      } else if (err.response?.data?.message) {
        // Axios error with direct message
        showError(err.response.data.message);
      } else if (err.response?.status) {
        // HTTP error without specific message
        showError(
          `Server error: ${err.response.status} ${
            err.response.statusText || ""
          }`
        );
      } else {
        // Fallback error message
        showError("Failed to save donation. Please try again.");
      }

      throw err;
    }
  };

  const handleConfirmSubmit = async () => {
    setLoading(true);
    setConfirmDialogOpen(false);
    try {
      await performSubmit(pendingSubmitData);
    } catch (err) {
      console.error("Confirm Submit Error:", err);
      // Error already handled in performSubmit, but ensure loading state is reset
    } finally {
      setLoading(false);
      setPendingSubmitData(null);
    }
  };

  const getContactLabel = (contact) => {
    if (contact.type === "INDIVIDUAL") {
      return `${contact.first_name} ${contact.last_name || ""}`.trim();
    }
    return contact.organization_name;
  };

  if (pageLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  // Helper function to format display values
  const getDisplayValue = (value, type = "text") => {
    if (!value && value !== 0) return "—";

    switch (type) {
      case "date":
        return new Date(value).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      case "currency":
        return `${formData.currency} ${parseFloat(value).toLocaleString()}`;
      case "boolean":
        return value ? "Yes" : "No";
      default:
        return value;
    }
  };

  const getFrequencyLabel = (freq) => {
    const labels = {
      one_time: "One Time",
      monthly: "Monthly",
      quarterly: "Quarterly",
      half_yearly: "Half Yearly",
      yearly: "Yearly",
    };
    return labels[freq] || freq;
  };

  const getDonationTypeLabel = (type) => {
    const labels = {
      one_time: "One-time",
      recurring: "Recurring",
      pledge: "Pledge",
    };
    return labels[type] || type;
  };

  const getStatusColor = (status) => {
    const colors = {
      active: "#10B981",
      paused: "#F59E0B",
      cancelled: "#EF4444",
      overdue: "#DC2626",
      completed: "#10B981",
    };
    return colors[status?.toLowerCase()] || "#6B7280";
  };

  // Get selected contact - check both from contacts list and from API response data
  const selectedContact = contacts.find(
    (c) => c.id === formData.contact_id
  ) || {
    id: formData.contact_id,
    first_name: formData.first_name,
    last_name: formData.last_name,
    organization_name: formData.organization_name,
    primary_email: formData.primary_email,
    type: formData.organization_name ? "ORGANIZATION" : "INDIVIDUAL",
  };

  // Main render - conditional based on mode
  return (
    <Box
      sx={{
        minHeight: "100vh",
      }}>
      {isViewMode ? (
        // View Mode Layout
        <Box>
          {/* Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <IconButton
                onClick={() => navigate("/donations")}
                size="small"
                sx={{
                  "&:hover": { color: "rgba(0, 0, 0, 1)" },
                }}>
                <ArrowBack fontSize="small" />
              </IconButton>
              <Typography
                variant="h6"
                sx={{
                  fontSize: "1.2rem",
                  fontWeight: 700,
                  color: "#000000",
                }}>
                Donation Details
              </Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              {canEdit && (
                <Tooltip title="Edit Donation" arrow>
                  <IconButton
                    onClick={handleEditClick}
                    sx={{
                      color: colors.text.primary,
                      "&:hover": {
                        color: colors.primary.main,
                        backgroundColor: "transparent",
                      },
                    }}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {canDelete && (
                <Tooltip title="Delete Donation" arrow>
                  <IconButton
                    onClick={handleDeleteClick}
                    sx={{
                      color: colors.text.primary,
                      "&:hover": {
                        color: colors.error.main,
                        backgroundColor: "transparent",
                      },
                    }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>

          {/* Content Cards */}
          <Grid container spacing={3}>
            {/* Single Card - Full Width */}
            <Grid item xs={12}>
              <Card
                sx={{
                  borderRadius: "4px",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                  p: 3,
                }}>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: colors.primary.main,
                    mb: 2,
                    pb: 1,
                    borderBottom: `2px solid ${colors.primary.main}`,
                  }}>
                  Donation Information
                </Typography>

                <Grid container spacing={3}>
                  {/* Left Column */}
                  <Grid item xs={12} md={6}>
                    <Box display="flex" flexDirection="column" gap={2}>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.text.secondary,
                            fontWeight: 500,
                            textTransform: "uppercase",
                            fontSize: "0.7rem",
                            letterSpacing: "0.5px",
                          }}>
                          Donor
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: colors.text.primary,
                            fontWeight: 500,
                            mt: 0.5,
                          }}>
                          {selectedContact
                            ? getContactLabel(selectedContact)
                            : "—"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.text.secondary,
                            fontWeight: 500,
                            textTransform: "uppercase",
                            fontSize: "0.7rem",
                            letterSpacing: "0.5px",
                          }}>
                          Donation Schema
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: colors.text.primary,
                            fontWeight: 500,
                            mt: 0.5,
                          }}>
                          {getDisplayValue(
                            formData.donation_schema?.name ||
                              formData.donation_schema_name
                          )}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.text.secondary,
                            fontWeight: 500,
                            textTransform: "uppercase",
                            fontSize: "0.7rem",
                            letterSpacing: "0.5px",
                          }}>
                          Amount
                        </Typography>
                        <Typography
                          variant="h5"
                          sx={{
                            color: colors.text.primary,
                            fontWeight: 700,
                            mt: 0.5,
                          }}>
                          {getDisplayValue(formData.amount, "currency")}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.text.secondary,
                            fontWeight: 500,
                            textTransform: "uppercase",
                            fontSize: "0.7rem",
                            letterSpacing: "0.5px",
                          }}>
                          Donation Date
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: colors.text.primary,
                            fontWeight: 500,
                            mt: 0.5,
                          }}>
                          {getDisplayValue(formData.donation_date, "date")}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            color: colors.text.secondary,
                            fontWeight: 500,
                            textTransform: "uppercase",
                            fontSize: "0.7rem",
                            letterSpacing: "0.5px",
                          }}>
                          Donation Type
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: colors.text.primary,
                            fontWeight: 500,
                            mt: 0.5,
                          }}>
                          {getDonationTypeLabel(formData.donation_type)}
                        </Typography>
                      </Box>

                      {formData.purpose && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.text.secondary,
                              fontWeight: 500,
                              textTransform: "uppercase",
                              fontSize: "0.7rem",
                              letterSpacing: "0.5px",
                            }}>
                            Purpose
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: colors.text.primary,
                              mt: 0.5,
                              whiteSpace: "pre-wrap",
                            }}>
                            {formData.purpose}
                          </Typography>
                        </Box>
                      )}

                      {formData.notes && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.text.secondary,
                              fontWeight: 500,
                              textTransform: "uppercase",
                              fontSize: "0.7rem",
                              letterSpacing: "0.5px",
                            }}>
                            Notes
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: colors.text.primary,
                              mt: 0.5,
                              whiteSpace: "pre-wrap",
                            }}>
                            {formData.notes}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  {/* Right Column */}
                  <Grid item xs={12} md={6}>
                    <Box display="flex" flexDirection="column" gap={2}>
                      {formData.donation_type === "recurring" && (
                        <>
                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: colors.text.secondary,
                                fontWeight: 500,
                                textTransform: "uppercase",
                                fontSize: "0.7rem",
                                letterSpacing: "0.5px",
                              }}>
                              Frequency
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                color: colors.text.primary,
                                fontWeight: 500,
                                mt: 0.5,
                              }}>
                              {getFrequencyLabel(formData.frequency)}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: colors.text.secondary,
                                fontWeight: 500,
                                textTransform: "uppercase",
                                fontSize: "0.7rem",
                                letterSpacing: "0.5px",
                              }}>
                              Reminder Start Date
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                color: colors.text.primary,
                                fontWeight: 500,
                                mt: 0.5,
                              }}>
                              {getDisplayValue(
                                formData.recurring_start_date,
                                "date"
                              )}
                            </Typography>
                          </Box>

                          <Box>
                            <Typography
                              variant="caption"
                              sx={{
                                color: colors.text.secondary,
                                fontWeight: 500,
                                textTransform: "uppercase",
                                fontSize: "0.7rem",
                                letterSpacing: "0.5px",
                              }}>
                              Reminder End Date
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                color: colors.text.primary,
                                fontWeight: 500,
                                mt: 0.5,
                              }}>
                              {getDisplayValue(
                                formData.recurring_end_date,
                                "date"
                              ) || "No end date"}
                            </Typography>
                          </Box>
                        </>
                      )}

                      {formData.recorded_by_name && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.text.secondary,
                              fontWeight: 500,
                              textTransform: "uppercase",
                              fontSize: "0.7rem",
                              letterSpacing: "0.5px",
                            }}>
                            Recorded By
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: colors.text.primary,
                              fontWeight: 500,
                              mt: 0.5,
                            }}>
                            {formData.recorded_by_name}
                          </Typography>
                        </Box>
                      )}

                      {(formData.created_at || formData.updated_at) && (
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: colors.text.secondary,
                              fontWeight: 500,
                              textTransform: "uppercase",
                              fontSize: "0.7rem",
                              letterSpacing: "0.5px",
                            }}>
                            {formData.updated_at !== formData.created_at
                              ? "Last Updated"
                              : "Created"}
                          </Typography>
                          <Typography
                            variant="body1"
                            sx={{
                              color: colors.text.primary,
                              fontWeight: 500,
                              mt: 0.5,
                            }}>
                            {getDisplayValue(
                              formData.updated_at !== formData.created_at
                                ? formData.updated_at
                                : formData.created_at,
                              "date"
                            )}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>
                </Grid>
              </Card>
            </Grid>
          </Grid>
        </Box>
      ) : (
        // Edit Mode Layout (form)
        <Box
          sx={{
            minHeight: "100vh",
            fontSize: "0.875rem",
            "& .MuiTypography-root": {
              fontSize: "inherit",
            },
            "& .MuiTypography-h6": {
              fontSize: "1.25rem",
            },
            "& .MuiTextField-root input": {
              fontSize: "0.8125rem",
            },
            "& .MuiInputLabel-root": {
              fontSize: "0.8125rem",
            },
          }}>
          {/* Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={3}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <IconButton
                onClick={() => navigate("/donations")}
                size="small"
                sx={{
                  "&:hover": { color: "rgba(0, 0, 0, 1)" },
                }}>
                <ArrowBack fontSize="small" />
              </IconButton>
              <Typography
                variant="h6"
                sx={{
                  textTransform: "capitalize",
                  fontWeight: 700,
                  color: "#000000",
                }}>
                {id && id !== "new" ? "Edit Donation" : "Add New Donation"}
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              {contactsPermission && (
                <Button
                  variant="outlined"
                  startIcon={<PersonAdd fontSize="small" />}
                  onClick={() => {
                    // Save current form state to Redux before navigating
                    dispatch(saveDonationFormState(formData));
                    navigate("/contacts/new?from=donation");
                  }}
                  disabled={loading}
                  sx={{
                    textTransform: "none",
                    borderColor: colors.primary.main,
                    color: colors.primary.main,
                    borderRadius: "4px",
                    fontWeight: 600,
                    "&:hover": {
                      borderColor: colors.primary.dark,
                      backgroundColor: colors.primary.main + "10",
                    },
                  }}>
                  Add Contact
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={
                  loading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <Save fontSize="small" />
                  )
                }
                onClick={handleSubmit}
                disabled={loading}
                sx={{
                  borderRadius: "4px",
                  textTransform: "none",
                  fontWeight: 600,
                  backgroundColor: colors.primary.main,
                  "&:hover": {
                    backgroundColor: colors.primary.dark,
                  },
                  "&.Mui-disabled": {
                    backgroundColor: colors.action.disabledBackground,
                    color: colors.text.disabled,
                  },
                }}>
                Save Donation
              </Button>
            </Box>
          </Box>

          {/* Form */}
          <Card
            sx={{
              borderRadius: "4px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
              p: 3,
            }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2.5}>
                <Grid item xs={12}>
                  <Autocomplete
                    options={contacts}
                    getOptionLabel={getContactLabel}
                    value={
                      contacts.find((c) => c.id === formData.contact_id) || null
                    }
                    onChange={(e, newValue) => {
                      const newContactId = newValue?.id || "";
                      setFormData((prev) => ({
                        ...prev,
                        contact_id: newContactId,
                      }));

                      // Clear search term when a contact is selected
                      if (newValue) {
                        setContactSearchTerm("");
                      }

                      // Validate
                      const error = validateField("contact_id", newContactId);
                      setValidationErrors((prev) => ({
                        ...prev,
                        contact_id: error,
                      }));
                    }}
                    onInputChange={(event, newInputValue, reason) => {
                      // Update search term when user types
                      if (reason === "input") {
                        setContactSearchTerm(newInputValue);
                      }
                    }}
                    filterOptions={(x) => x} // Disable client-side filtering since we're doing server-side search
                    noOptionsText={
                      loadingContacts
                        ? "Searching..."
                        : contactSearchTerm
                        ? "No contacts found"
                        : "Type to search contacts"
                    }
                    slotProps={{
                      paper: {
                        sx: {
                          borderRadius: "4px",
                          "& .MuiAutocomplete-option": {
                            backgroundColor: "transparent !important",
                            "&:hover": {
                              backgroundColor: `${colors.action.hover} !important`,
                            },
                            "&[aria-selected='true']": {
                              backgroundColor: `${colors.action.hover} !important`,
                            },
                          },
                        },
                      },
                    }}
                    ListboxProps={{
                      onScroll: (event) => {
                        const listboxNode = event.currentTarget;
                        if (
                          !loadingContacts &&
                          hasMoreContacts &&
                          listboxNode.scrollTop + listboxNode.clientHeight >=
                            listboxNode.scrollHeight - 50
                        ) {
                          loadContacts(
                            contactsPage + 1,
                            debouncedContactSearch
                          );
                        }
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Donor"
                        placeholder="Type to search contacts..."
                        required
                        error={
                          !!validationErrors.contact_id ||
                          !!fieldErrors.contact_id
                        }
                        helperText={
                          validationErrors.contact_id || fieldErrors.contact_id
                        }
                        FormHelperTextProps={{
                          sx: { marginLeft: 0 },
                        }}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingContacts ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <DonationSchemaAutocomplete
                    options={donationSchemas}
                    value={formData.donation_schema}
                    onChange={(event) => {
                      // Autocomplete returns event as first arg, and value as second
                      // But our custom component mimicks TextField event signature in first arg, but also passes value in second
                      // Let's rely on the synthetic event we built in the child component if possible,
                      // OR just use the value directly if standard Autocomplete signature is used.

                      // Our refactored component sends (syntheticEvent, newValue)
                      // syntheticEvent.target.value is the newValue object.

                      const newValue = event.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        donation_schema: newValue,
                      }));
                      // Clear validation error when value changes
                      if (newValue) {
                        setValidationErrors((prev) => ({
                          ...prev,
                          donation_schema: "",
                        }));
                        setFieldErrors((prev) => ({
                          ...prev,
                          donation_schema: "",
                        }));
                      }
                    }}
                    onSchemaCreated={(newSchema) => {
                      // When new schema is created, we can either reload everything or just append.
                      // The component callback already selects it.
                      // We should probably append it to our local state if not there.
                      setDonationSchemas((prev) => {
                        if (prev.some((s) => s.id === newSchema.id))
                          return prev;
                        return [...prev, newSchema];
                      });
                    }}
                    required
                    error={
                      !!validationErrors.donation_schema ||
                      !!fieldErrors.donation_schema
                    }
                    helperText={
                      validationErrors.donation_schema ||
                      fieldErrors.donation_schema ||
                      "Select the type of donation according to spiritual/charitable category"
                    }
                    loading={loadingSchemas}
                    hasMore={hasMoreSchemas}
                    onLoadMore={() => loadDonationSchemas(schemasPage + 1)}
                  />
                </Grid>

                <Grid item xs={3}>
                  <TextField
                    select
                    fullWidth
                    label="Currency"
                    name="currency"
                    value={formData.currency}
                    onChange={handleChange}
                    required
                    error={
                      !!validationErrors.currency || !!fieldErrors.currency
                    }
                    helperText={
                      validationErrors.currency || fieldErrors.currency
                    }
                    FormHelperTextProps={{
                      sx: { marginLeft: 0 },
                    }}
                    SelectProps={{
                      MenuProps: {
                        PaperProps: {
                          sx: {
                            borderRadius: "4px",
                          },
                        },
                      },
                    }}>
                    <MenuItem value="INR">INR (₹)</MenuItem>
                    <MenuItem value="USD">USD ($)</MenuItem>
                    <MenuItem value="EUR">EUR (€)</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={9}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Amount"
                    name="amount"
                    value={formData.amount}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Only allow whole numbers (no decimals) and prevent starting with 0
                      if (
                        value === "" ||
                        (/^\d+$/.test(value) && !value.startsWith("0"))
                      ) {
                        handleChange(e);
                      }
                    }}
                    onKeyDown={(e) => {
                      // Prevent decimal point, minus sign, and 'e' (scientific notation)
                      if (
                        e.key === "." ||
                        e.key === "-" ||
                        e.key === "e" ||
                        e.key === "E"
                      ) {
                        e.preventDefault();
                      }
                    }}
                    required
                    error={!!validationErrors.amount || !!fieldErrors.amount}
                    helperText={validationErrors.amount || fieldErrors.amount}
                    FormHelperTextProps={{
                      sx: { marginLeft: 0 },
                    }}
                    inputProps={{
                      min: 1,
                      step: 1,
                      pattern: "[1-9][0-9]*",
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Donation Date"
                    name="donation_date"
                    value={formData.donation_date}
                    onChange={handleChange}
                    InputLabelProps={{ shrink: true }}
                    required
                    error={
                      !!validationErrors.donation_date ||
                      !!fieldErrors.donation_date
                    }
                    helperText={
                      validationErrors.donation_date ||
                      fieldErrors.donation_date
                    }
                    FormHelperTextProps={{
                      sx: { marginLeft: 0 },
                    }}
                    onClick={(e) => {
                      e.target.focus();
                      setTimeout(() => e.target.showPicker?.(), 0);
                    }}
                    sx={{ cursor: "pointer" }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        color: colors.text.primary,
                        mb: 1.5,
                      }}>
                      Donation Type
                    </Typography>
                    <RadioGroup
                      name="donation_type"
                      value={formData.donation_type}
                      onChange={handleChange}
                      row>
                      <FormControlLabel
                        value="one_time"
                        control={<Radio />}
                        label="One-time"
                      />
                      <FormControlLabel
                        value="recurring"
                        control={<Radio />}
                        label="Recurring"
                      />
                      <FormControlLabel
                        value="pledge"
                        control={<Radio />}
                        label="Pledge"
                      />
                    </RadioGroup>
                  </Box>
                </Grid>

                {formData.donation_type === "recurring" ? (
                  <>
                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            color: colors.primary.main,
                          }}>
                          Recurring Donation Details
                        </Typography>
                        <Tooltip
                          title="Recurring donations are automatically set reminder about the donation. Set the start date and status to manage the recurring schedule."
                          arrow
                          placement="right">
                          <InfoOutlined
                            sx={{
                              fontSize: "0.95rem",
                              color: colors.text.secondary,
                              cursor: "pointer",
                              "&:hover": {
                                color: colors.primary.main,
                              },
                            }}
                          />
                        </Tooltip>
                      </Box>
                    </Grid>

                    <Grid item xs={2}>
                      <TextField
                        select
                        fullWidth
                        label="Frequency"
                        name="frequency"
                        value={formData.frequency}
                        onChange={handleChange}
                        required
                        error={
                          !!validationErrors.frequency ||
                          !!fieldErrors.frequency
                        }
                        helperText={
                          validationErrors.frequency ||
                          fieldErrors.frequency ||
                          "How often the donation recurs"
                        }
                        FormHelperTextProps={{
                          sx: { marginLeft: 0, marginRight: 0 },
                        }}
                        SelectProps={{
                          MenuProps: {
                            PaperProps: {
                              sx: {
                                borderRadius: "4px",
                              },
                            },
                          },
                        }}>
                        <MenuItem value="monthly">Monthly</MenuItem>
                        <MenuItem value="quarterly">Quarterly</MenuItem>
                        <MenuItem value="half_yearly">Half Yearly</MenuItem>
                        <MenuItem value="yearly">Yearly</MenuItem>
                      </TextField>
                    </Grid>

                    <Grid item xs={5}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Reminder Start Date"
                        name="recurring_start_date"
                        value={formData.recurring_start_date}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        required
                        error={
                          !!validationErrors.recurring_start_date ||
                          !!fieldErrors.recurring_start_date
                        }
                        helperText={
                          validationErrors.recurring_start_date ||
                          fieldErrors.recurring_start_date ||
                          "When reminder is going to starts"
                        }
                        FormHelperTextProps={{
                          sx: {
                            marginLeft: 0,
                            color:
                              validationErrors.recurring_start_date ||
                              fieldErrors.recurring_start_date
                                ? "error.main"
                                : "text.secondary",
                          },
                        }}
                        onClick={(e) => {
                          e.target.focus();
                          setTimeout(() => e.target.showPicker?.(), 0);
                        }}
                        sx={{ cursor: "pointer" }}
                      />
                    </Grid>

                    <Grid item xs={5}>
                      <TextField
                        fullWidth
                        type="date"
                        label="Reminder End Date"
                        name="recurring_end_date"
                        value={formData.recurring_end_date}
                        onChange={handleChange}
                        InputLabelProps={{ shrink: true }}
                        error={
                          !!validationErrors.recurring_end_date ||
                          !!fieldErrors.recurring_end_date
                        }
                        helperText={
                          validationErrors.recurring_end_date ||
                          fieldErrors.recurring_end_date ||
                          "When reminder should stop (optional)"
                        }
                        FormHelperTextProps={{
                          sx: {
                            marginLeft: 0,
                            color:
                              validationErrors.recurring_end_date ||
                              fieldErrors.recurring_end_date
                                ? "error.main"
                                : "text.secondary",
                          },
                        }}
                        onClick={(e) => {
                          e.target.focus();
                          setTimeout(() => e.target.showPicker?.(), 0);
                        }}
                        sx={{ cursor: "pointer" }}
                      />
                    </Grid>
                  </>
                ) : null}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Purpose"
                    name="purpose"
                    value={formData.purpose}
                    onChange={handleChange}
                    multiline
                    rows={2}
                    error={!!fieldErrors.purpose}
                    helperText={
                      fieldErrors.purpose ||
                      `${formData.purpose?.length || 0}/500`
                    }
                    FormHelperTextProps={{
                      sx: { marginLeft: 0, marginRight: 0, textAlign: "right" },
                    }}
                    inputProps={{
                      maxLength: 500,
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    multiline
                    rows={2}
                    error={!!fieldErrors.notes}
                    helperText={
                      fieldErrors.notes || `${formData.notes?.length || 0}/1000`
                    }
                    FormHelperTextProps={{
                      sx: { marginLeft: 0, marginRight: 0, textAlign: "right" },
                    }}
                    inputProps={{
                      maxLength: 1000,
                    }}
                  />
                </Grid>
              </Grid>
            </form>
          </Card>
        </Box>
      )}

      {/* Donation Confirmation Dialog */}
      <DonationConfirmationWrapper
        open={confirmDialogOpen}
        onClose={() => {
          setConfirmDialogOpen(false);
          setPendingSubmitData(null);
        }}
        onConfirm={handleConfirmSubmit}
        title="Update Recurring Donation"
        message="You are about to update this recurring donation. This will also affect the associated reminder."
        warningMessage="The reminder's date and status will be updated to match the donation changes."
        loading={loading}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleDeleteConfirm}
        itemName="donation"
        deleting={deleting}>
        <Typography
          variant="body2"
          sx={{ color: colors.text.secondary, px: 2, textAlign: "center" }}>
          This action cannot be undone. The donation and any associated
          reminders will be permanently deleted.
        </Typography>
      </DeleteConfirmationDialog>
    </Box>
  );
}
